import React from "react";
import { cn } from "utils/utils";

interface SidebarAsideProps {
  sidebarHeader?: React.ReactNode;
  /** Matches SidebarPageLayout's prop — see the max-height note below. */
  fullHeight?: boolean;
  children: React.ReactNode;
}

interface SidebarPageLayoutProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  sidebarHeader?: React.ReactNode;
  fullHeight?: boolean;
  className?: string;
}

export function SidebarAside({
  sidebarHeader,
  fullHeight,
  children,
}: SidebarAsideProps) {
  return (
    <aside className="hidden w-64 shrink-0 md:block xl:w-80">
      <div
        className={cn(
          "sticky top-4 flex flex-col gap-3",
          // In a fullHeight row the aside is already bounded by the row, so
          // bound the sticky column to the aside. A viewport-relative cap can
          // exceed the row and spill over whatever follows it — only visible
          // once the sidebar's content is taller than the row.
          fullHeight ? "max-h-full" : "max-h-[calc(100svh-2rem)]"
        )}
      >
        {sidebarHeader}
        <div className="card shadow-xs border-base-content/20 min-h-0 overflow-y-auto border">
          <div className="card-body p-2">{children}</div>
        </div>
      </div>
    </aside>
  );
}

export default function SidebarPageLayout({
  sidebar,
  sidebarHeader,
  fullHeight,
  className,
  children,
}: SidebarPageLayoutProps) {
  return (
    <div className={cn("flex gap-2", fullHeight && "h-full", className)}>
      <SidebarAside sidebarHeader={sidebarHeader} fullHeight={fullHeight}>
        {sidebar}
      </SidebarAside>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
